import { ColumnDef } from "@tanstack/react-table";
import { ArrowRightInCircle } from "assets/icons";
import { useLanguage } from "hooks";
import { ProductCell, ProductColorLabel } from "modules/products";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon, ItemsTable } from "ui";

import { PriceInsightsListItem } from "../../insights.types";
import { PriceInsightsTableProps } from "./PriceInsightsTable.types";

import styles from "./PriceInsightsTable.module.scss";

const PriceInsightsTable = ({
  products = [],
  sorting,
  setSorting,
  isLoading
}: PriceInsightsTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const columns: Array<
    ColumnDef<PriceInsightsListItem, PriceInsightsListItem[]>
  > = useMemo(
    () => [
      {
        header: t("Product"),
        size: 35,
        minSize: 35,
        cell: ({ row }) => (
          <ProductCell
            image={products[row.index].image}
            name={products[row.index].name}
            imageClassName={styles.table_productImage}
            subtitle={
              <>
                <span>{products[row.index].sku}</span>
                <span>·</span>
                <ProductColorLabel
                  colorValue={products[row.index].colorAttributeValue.value}
                />
              </>
            }
          />
        )
      },
      {
        id: "lastSaleAt",
        accessorKey: "lastSaleAt",
        header: t("Last Sale"),
        size: 12,
        minSize: 12,
        cell: ({ row }) => (
          <pre>
            {`${moment(products[row.index].lastSaleAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(products[row.index].lastSaleAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "buyingPrice",
        accessorKey: "buyingPrice",
        header: t("Buying Price"),
        size: 12,
        minSize: 12,
        cell: ({ row }) =>
          products[row.index].buyingPrice.toLocaleString("en-US")
      },
      {
        id: "standardPrice",
        accessorKey: "standardPrice",
        header: t("Standard Price"),
        size: 12,
        minSize: 12,
        cell: ({ row }) =>
          products[row.index].standardPrice.toLocaleString("en-US")
      },
      {
        id: "averageSellingPrice",
        accessorKey: "averageSellingPrice",
        header: t("Average Selling Price"),
        size: 12,
        minSize: 12,
        cell: ({ row }) =>
          products[row.index].averageSellingPrice.toLocaleString("en-US")
      },
      {
        header: t("Sold Range"),
        size: 14,
        minSize: 14,
        cell: ({ row }) =>
          `${products[row.index].minSellingPrice.toLocaleString("en-US")} - ${products[row.index].maxSellingPrice.toLocaleString("en-US")}`
      },
      {
        id: "arrow",
        size: 3,
        minSize: 3,
        cell: () => <Icon as={ArrowRightInCircle} boxSize={24} />
      }
    ],
    [products, currentLanguage]
  );

  return (
    <ItemsTable
      className={styles.table}
      columns={columns}
      data={products}
      sorting={sorting}
      skeletonHeight={60}
      setSorting={setSorting}
      isLoading={isLoading}
      getLinkUrl={({ id }) => `/insights/price-insights/${id}`}
      linkState={{ productsListSearch: window.location.search }}
    />
  );
};

export default PriceInsightsTable;
